import { useMemo } from 'react';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { type AppState } from '../app.store';

export const userDataSelector = (state: AppState) => state.userData;
export const useUserDataSelector = () => useSelector(userDataSelector);

export const userDataEmailSelector = (state: AppState) => state.userData.email;
export const useUserDataEmailSelector = () => useSelector(userDataEmailSelector);

const TwoFASelector = (state: AppState) => state.userData['2fa'];
export const useTwoFASelector = () => useSelector(TwoFASelector);

const userDataUsagesSelector = (state: AppState) => state.userData.usage;
export const useUserDataUsagesSelector = () => useSelector(userDataUsagesSelector);

export const userCurrentClientSelector = (state: AppState) => state.userData.currentClient;
export const useUserCurrentClientSelector = () => useSelector(userCurrentClientSelector);

const googleApiOauthUserDataSelector = (state: AppState) => state.userData.googleApiOauth;
export const useGoogleApiOauthUserDataSelector = () => useSelector(googleApiOauthUserDataSelector);

export const userIdSelector = (state: AppState) => state.userData.id;
export const useUserIdSelector = () => useSelector<number | null>(userIdSelector);

const userLimitsSelector = (state: AppState) => state.userData.limits;
export const useUserLimitsSelector = () => useSelector(userLimitsSelector);

export const selectIsUserOverDirectionsApiLimit = (state: AppState) => {
  const usage = state.userData.usage.directionsApiRequests;
  const limit = state.userData.limits.directionsApiRequests;
  return usage >= limit;
};
export const useIsUserOverDirectionsApiLimit = () => useSelector(selectIsUserOverDirectionsApiLimit);

export const optimizedWMSTerritoriesEnabledSelector = (state: AppState) => state.userData.limits.wmsEnabled;
export const useOptimizedWMSTerritoriesEnabledSelector = () => useSelector(optimizedWMSTerritoriesEnabledSelector);

export const optimizedTerritoriesEnabledSelector = (state: AppState) => state.userData.limits.motEnabled;
export const useOptimizedTerritoriesEnabledSelector = () => useSelector(optimizedTerritoriesEnabledSelector);

const userRoleSelector = (state: AppState) => state.userData.role;
export const useUserRoleSelector = () => useSelector(userRoleSelector);

const isUserSignedInSelector = (state: AppState) => state.userData.isSignedIn;
export const useIsUserSignedInSelector = () => useSelector(isUserSignedInSelector);

const userSettingsSelector = (state: AppState) => state.userData.settings;

const currentClientUserSettingsSelector = (state: AppState) => {
  const settings = userSettingsSelector(state);
  const currentClientId = userCurrentClientSelector(state);
  if (settings && currentClientId) {
    return settings[currentClientId.id];
  }
  return null;
};
export const useCurrentClientUserSettingsSelector = () => useSelector(currentClientUserSettingsSelector);

export const clientMembersSelector = (state: AppState) => state.userData.clientMembers;
export const useClientMembers = () => useSelector(clientMembersSelector);
export const useClientOwner = () => useSelector(clientMembersSelector).find(m => m.role === 'Company Owner');
export const useClientIsNew = () => useSelector(userDataUsagesSelector).createdMaps === 0;

export const userDataActiveAddonsSelector = (state: AppState) => state.userData.limits.addons;
export const useUserDataActiveAddonsSelector = () => {
  const userAddons = useSelector(userDataActiveAddonsSelector);

  return useMemo(() => new Set(userAddons), [userAddons]);
};
